import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckCircle } from 'lucide-react'
import { useAuthStore } from '@/features/auth/stores/authStore'
import { useActivationKeys } from '@/features/solution-browser/hooks/useActivationKey'
import { fbTrackViewContent } from '@/shared/utils/facebookPixel'
import { useAllTestSeries } from '../hooks'
import TestSeriesCard from '../components/TestSeriesCard'
import ActivateSeriesDialog from '../components/ActivateSeriesDialog'
import type { TestSeriesResponse } from '../types'
import { buildSeriesAccessMap, type SeriesAccess } from '../utils/seriesAccess'

const TestSeriesListPage = () => {
  const navigate = useNavigate()
  const userId = useAuthStore((s) => s.userId) ?? undefined
  const [activatingSeries, setActivatingSeries] = useState<TestSeriesResponse | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const { data: seriesList, isLoading: isLoadingSeries, error: seriesError, refetch: refetchSeries } = useAllTestSeries()
  const { data: keys, isError: isKeysError, refetch: refetchKeys } = useActivationKeys(userId)

  useEffect(() => {
    fbTrackViewContent('test_series', undefined, 'Test Series')
  }, [])

  const accessBySeries = useMemo(() => buildSeriesAccessMap(keys ?? []), [keys])

  const getAccess = (seriesCode: string): SeriesAccess | null =>
    keys ? accessBySeries.get(seriesCode) ?? 'NONE' : null

  const handleBrowse = (series: TestSeriesResponse) => {
    navigate(`/test-series/${series.seriesCode}`)
  }

  const handleActivated = (series: TestSeriesResponse) => {
    setActivatingSeries(null)
    setSuccessMessage(`Activation successful for ${series.seriesName}.`)
  }

  if (isLoadingSeries) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />
      </div>
    )
  }

  if (seriesError) {
    return (
      <div className="mx-auto max-w-2xl rounded-lg border border-red-200 bg-red-50 p-6">
        <h2 className="mb-2 text-lg font-semibold text-red-800">Error Loading Test Series</h2>
        <p className="mb-4 text-red-700">{seriesError.message || 'Failed to load test series'}</p>
        <button
          onClick={() => refetchSeries()}
          className="rounded bg-red-600 px-4 py-2 text-white transition-colors hover:bg-red-700"
        >
          Try Again
        </button>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="mb-1 text-2xl font-bold text-gray-900 sm:text-3xl">Test Series</h1>
        <p className="text-sm text-gray-600 sm:text-base">
          Activate a test series with the key from your book or email, then browse its tests.
        </p>
      </div>

      {successMessage && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
          <CheckCircle className="h-4 w-4 flex-shrink-0" />
          <span>{successMessage}</span>
        </div>
      )}

      {isKeysError && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-orange-200 bg-orange-50 p-3 text-sm text-orange-800">
          <span>We couldn't load your activated series.</span>
          <button onClick={() => refetchKeys()} className="font-medium underline">
            Retry
          </button>
        </div>
      )}

      {seriesList && seriesList.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
          {seriesList.map((series) => (
            <TestSeriesCard
              key={series.seriesId}
              series={series}
              access={getAccess(series.seriesCode)}
              onBrowse={handleBrowse}
              onActivate={(s) => {
                setSuccessMessage(null)
                setActivatingSeries(s)
              }}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-lg bg-white p-8 text-center text-gray-500 sm:p-12">
          No test series available yet.
        </div>
      )}

      {userId && (
        <ActivateSeriesDialog
          userId={userId}
          series={activatingSeries}
          onClose={() => setActivatingSeries(null)}
          onSuccess={handleActivated}
        />
      )}
    </div>
  )
}

export default TestSeriesListPage
